import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, notification } from "antd";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  IoCameraOutline,
  IoCogOutline,
  IoCopyOutline,
  IoCubeOutline,
  IoGlobeOutline,
  IoHeadsetOutline,
  IoInformationCircleOutline,
  IoMailOutline,
  IoMicOutline,
  IoFlashOutline,
  IoPaperPlaneOutline,
  IoRefreshOutline,
  IoSpeedometerOutline,
  IoVolumeHighOutline,
} from "react-icons/io5";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import "../../components/lobby/lobbyModern.css";
import "./settingsV2.css";
import { useAuth } from "../../services/AuthContext";
import { LANGUAGE_OPTIONS, getLanguageCopy, normalizeLanguage } from "../../services/language";
import { setLanguage as setI18nLanguage, t as i18nT, t as tKey } from "../../i18n";
import {
  DEFAULT_BLOCK_ANIMATION_SPEED,
  DEFAULT_PREFERENCES,
  MAX_BLOCK_ANIMATION_SPEED,
  MIN_BLOCK_ANIMATION_SPEED,
  getPreferencesStorageKey,
  normalizeBlockAnimationSpeed,
  parsePreferences,
  writeGlobalBlockAnimationSpeed,
  writeUserPreferences,
} from "../../services/userSettings";
import {
  VOICE_CONTROL_STATUS,
  createVoiceControlController,
  getVoiceControlStatusText,
  getVoiceRecognitionLanguage,
} from "../../services/voiceControl";

const SECTIONS = ["general", "language", "support"];
const SUPPORT_EMAIL = "info@juegoxima.com";

const SETTINGS_COPY = {
  backToLobby: { es: "Volver al lobby", en: "Back to lobby", ru: "Вернуться в лобби", de: "Zurück zur Lobby" },
  gameTitle: {
    es: "Preferencias del juego",
    en: "Game preferences",
    ru: "Настройки игры",
    de: "Spieleinstellungen",
  },
  deviceOnly: {
    es: "Se aplican solo a este dispositivo.",
    en: "They only apply to this device.",
    ru: "Применяются только на этом устройстве.",
    de: "Gilt nur für dieses Gerät.",
  },
  gameSoundDescription: {
    es: "Activa o desactiva todos los sonidos del juego.",
    en: "Turn all game sounds on or off.",
    ru: "Включает или выключает все звуки игры.",
    de: "Schaltet alle Spielsounds ein oder aus.",
  },
  animationsDescription: {
    es: "Habilita o deshabilita las animaciones del juego.",
    en: "Enable or disable the game animations.",
    ru: "Включает или выключает анимации игры.",
    de: "Aktiviert oder deaktiviert die Spielanimationen.",
  },
  cameraFollowShort: {
    es: "Seguimiento de cámara",
    en: "Camera follow",
    ru: "Слежение камеры",
    de: "Kameraverfolgung",
  },
  cameraFollowDescription: {
    es: "La cámara seguirá automáticamente la acción.",
    en: "The camera will follow the action automatically.",
    ru: "Камера будет автоматически следить за действием.",
    de: "Die Kamera folgt automatisch dem Geschehen.",
  },
  premove: {
    es: "Jugada anticipada",
    en: "Premove",
    ru: "Предварительный ход",
    de: "Vorauszug",
  },
  premoveDescription: {
    es: "Marca tu próxima jugada mientras juega otro; se hace sola al llegar tu turno si sigue siendo legal.",
    en: "Mark your next move while others play; it is played automatically on your turn if still legal.",
    ru: "Отметьте следующий ход, пока ходят другие; он сыграется сам в ваш ход, если ещё допустим.",
    de: "Markiere deinen nächsten Zug, während andere spielen; er wird in deinem Zug automatisch gespielt, wenn er noch legal ist.",
  },
  blockAnimationSpeedDescription: {
    es: "Bloques y dados.",
    en: "Blocks and dice.",
    ru: "Блоки и кубики.",
    de: "Blöcke und Würfel.",
  },
  voiceControlShortDescription: {
    es: "Mueve, gira el tablero y la cámara con tu voz. No se guarda audio.",
    en: "Move, rotate the board and the camera with your voice. No audio is stored.",
    ru: "Ходите, вращайте доску и камеру голосом. Аудио не сохраняется.",
    de: "Ziehe, drehe das Brett und die Kamera mit deiner Stimme. Es wird kein Audio gespeichert.",
  },
  languageDescription: {
    es: "Cambia el idioma de toda la interfaz. No afecta a otros jugadores.",
    en: "Changes the language of the whole interface. It does not affect other players.",
    ru: "Меняет язык всего интерфейса. Не влияет на других игроков.",
    de: "Ändert die Sprache der ganzen Oberfläche. Andere Spieler sind nicht betroffen.",
  },
  copyEmail: { es: "Copiar correo", en: "Copy email", ru: "Скопировать почту", de: "E-Mail kopieren" },
  emailCopied: { es: "Correo copiado", en: "Email copied", ru: "Почта скопирована", de: "E-Mail kopiert" },
  sending: { es: "Enviando...", en: "Sending...", ru: "Отправка...", de: "Wird gesendet..." },
  autoSaveNote: {
    es: "Los cambios se guardan solos al instante. Puedes volver a los valores de fábrica con “Restablecer”.",
    en: "Changes are saved instantly. You can go back to the default values with “Reset”.",
    ru: "Изменения сохраняются сразу. Вернуть заводские значения можно кнопкой «Сбросить».",
    de: "Änderungen werden sofort gespeichert. Mit „Zurücksetzen“ kehrst du zu den Standardwerten zurück.",
  },
  sectionDescription: {
    es: "Configura idioma, soporte y preferencias del juego.",
    en: "Configure language, support, and game preferences.",
    ru: "Настройте язык, поддержку и параметры игры.",
    de: "Sprache, Support und Spieleinstellungen konfigurieren.",
  },
  generalTitle: {
    es: "Preferencias generales",
    en: "General preferences",
    ru: "Общие настройки",
    de: "Allgemeine Einstellungen",
  },
  generalDescription: {
    es: "Opciones para personalizar tu experiencia.",
    en: "Options to personalize your experience.",
    ru: "Параметры для персонализации вашего опыта.",
    de: "Optionen zur Personalisierung Ihrer Erfahrung.",
  },
  gameSound: { es: "Sonido del juego", en: "Game sound", ru: "Звук игры", de: "Spielsound" },
  activated: { es: "Activado", en: "Activated", ru: "Активировано", de: "Aktiviert" },
  deactivated: { es: "Desactivado", en: "Deactivated", ru: "Отключено", de: "Deaktiviert" },
  animations: { es: "Animaciones", en: "Animations", ru: "Анимации", de: "Animationen" },
  activatedPlural: { es: "Activadas", en: "Activated", ru: "Включены", de: "Aktiviert" },
  deactivatedPlural: { es: "Desactivadas", en: "Deactivated", ru: "Выключены", de: "Deaktiviert" },
  autoEnterLastMode: {
    es: "Auto entrar al ultimo modo",
    en: "Auto enter last mode",
    ru: "Автоматический вход в последний режим",
    de: "Automatisch letzten Modus öffnen",
  },
  compactRightPanel: {
    es: "Panel derecho compacto",
    en: "Compact right panel",
    ru: "Компактная правая панель",
    de: "Kompakte rechte Seite",
  },
  cameraFollow: {
    es: "Activar seguimiento de cámara",
    en: "Enable camera follow",
    ru: "Включить слежение камеры",
    de: "Kameraverfolgung aktivieren",
  },
  voiceControl: {
    es: "Control de voz",
    en: "Voice control",
    ru: "Voice control",
    de: "Sprachsteuerung",
  },
  voiceControlDescription: {
    es: "Permite controlar movimientos, camara y rotacion del tablero mediante comandos de voz.",
    en: "Control movement, camera, and board rotation with voice commands.",
    ru: "Control movement, camera, and board rotation with voice commands.",
    de: "Steuert Bewegung, Kamera und Brettrotation per Sprachbefehl.",
  },
  voiceControlPrivacy: {
    es: "No se guarda audio ni historial permanente de transcripciones.",
    en: "Audio and permanent transcription history are not stored.",
    ru: "Audio and permanent transcription history are not stored.",
    de: "Audio und dauerhafte Transkriptionsverlaeufe werden nicht gespeichert.",
  },
  voicePermissionDeniedDescription: {
    es: "No se pudo activar el control de voz porque el permiso del microfono fue rechazado.",
    en: "Voice control could not be enabled because microphone permission was denied.",
    ru: "Voice control could not be enabled because microphone permission was denied.",
    de: "Die Sprachsteuerung konnte nicht aktiviert werden, weil die Mikrofonberechtigung abgelehnt wurde.",
  },
  voicePermissionConfirmTitle: {
    es: "Activar control de voz",
    en: "Enable voice control",
    ru: "Enable voice control",
    de: "Sprachsteuerung aktivieren",
  },
  voicePermissionConfirmDescription: {
    es: "XIMA usara el microfono para escuchar comandos de juego. No guardamos audio.",
    en: "XIMA will use the microphone to listen for game commands. Audio is not stored.",
    ru: "XIMA will use the microphone to listen for game commands. Audio is not stored.",
    de: "XIMA nutzt das Mikrofon fuer Spielbefehle. Audio wird nicht gespeichert.",
  },
  voicePermissionConfirmOk: {
    es: "Activar microfono",
    en: "Enable microphone",
    ru: "Enable microphone",
    de: "Mikrofon aktivieren",
  },
  voicePermissionConfirmCancel: {
    es: "Ahora no",
    en: "Not now",
    ru: "Not now",
    de: "Jetzt nicht",
  },
  voicePermissionCancelledDescription: {
    es: "No se activo el microfono.",
    en: "The microphone was not enabled.",
    ru: "The microphone was not enabled.",
    de: "Das Mikrofon wurde nicht aktiviert.",
  },
  voiceUnavailableDescription: {
    es: "El control de voz no esta disponible en este dispositivo.",
    en: "Voice control is not available on this device.",
    ru: "Voice control is not available on this device.",
    de: "Die Sprachsteuerung ist auf diesem Geraet nicht verfuegbar.",
  },
  blockAnimationSpeed: {
    es: "Velocidad de animaciones",
    en: "Animation speed",
    ru: "Скорость анимации",
    de: "Animationsgeschwindigkeit",
  },
  blockAnimationSlow: { es: "Lenta", en: "Slow", ru: "Медленно", de: "Langsam" },
  blockAnimationNormal: { es: "Normal", en: "Normal", ru: "Нормально", de: "Normal" },
  blockAnimationFast: { es: "Rápida", en: "Fast", ru: "Быстро", de: "Schnell" },
  yes: { es: "Si", en: "Yes", ru: "Да", de: "Ja" },
  no: { es: "No", en: "No", ru: "Нет", de: "Nein" },
  reset: { es: "Restablecer", en: "Reset", ru: "Сбросить", de: "Zurücksetzen" },
  supportDescription: {
    es: "Reporta un error o pide ayuda al equipo.",
    en: "Report errors or request help from the team.",
    ru: "Сообщите об ошибках или запросите помощь команды.",
    de: "Fehler melden oder Hilfe vom Team anfordern.",
  },
  supportEmail: {
    es: "Email de soporte",
    en: "Support email",
    ru: "Электронная почта поддержки",
    de: "Support-E-Mail",
  },
  subject: { es: "Asunto", en: "Subject", ru: "Тема", de: "Betreff" },
  subjectPlaceholder: {
    es: "Escribe el asunto de tu consulta...",
    en: "Write the subject of your request...",
    ru: "Напишите тему вашего обращения...",
    de: "Schreibe den Betreff deiner Anfrage...",
  },
  describeProblem: {
    es: "Describe tu problema",
    en: "Describe your problem",
    ru: "Опишите вашу проблему",
    de: "Beschreibe dein Problem",
  },
  describeProblemPlaceholder: {
    es: "Cuéntanos con detalle para poder ayudarte...",
    en: "Tell us the details so we can help you...",
    ru: "Расскажите подробнее, чтобы мы могли вам помочь...",
    de: "Erzähl uns die Details, damit wir dir helfen können...",
  },
};

const SETTINGS_LANGUAGE_VISUALS = {
  es: {
    title: "ESPAÑOL",
    description: "Interfaz en español",
  },
  en: {
    title: "ENGLISH",
    description: "Interface in English",
  },
  ru: {
    title: "РУССКИЙ",
    description: "Интерфейс на русском",
  },
  de: {
    title: "DEUTSCH",
    description: "Interface auf Deutsch",
  },
};


const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user } = useAuth();
  const outletContext = useOutletContext() || {};

  const language = normalizeLanguage(outletContext.language);
  const setLanguage =
    typeof outletContext.setLanguage === "function" ? outletContext.setLanguage : () => {};
  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);
  const ui = useCallback(
    (key) => SETTINGS_COPY[key]?.[language] || SETTINGS_COPY[key]?.en || "",
    [language]
  );

  const fromLobbySource = String(
    searchParams.get("fromLobbySource") || location.state?.fromLobbySource || ""
  ).toLowerCase();
  const settingsReturnRoute = fromLobbySource === "settings" ? "/game/lobby" : "/game";
  const activeSectionFromUrl = String(searchParams.get("section") || "general").toLowerCase();
  const activeSection = SECTIONS.includes(activeSectionFromUrl)
    ? activeSectionFromUrl
    : "general";

  const routeActions = useModernRouteActions();
  const generalRef = useRef(null);
  const languageRef = useRef(null);
  const supportRef = useRef(null);

  // Igual que Estadísticas/Tienda: se esconde el menú antiguo y queda solo el del diseño Clásico.
  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active", "xaj-page-active");
    return () => document.body.classList.remove("xima-lobby-modern-active", "xaj-page-active");
  }, []);

  useEffect(() => {
    if (activeSection === "general") return undefined;
    const target = activeSection === "language" ? languageRef.current : supportRef.current;
    const timer = window.setTimeout(() => {
      target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [activeSection]);

  const preferencesStorageKey = useMemo(
    () => getPreferencesStorageKey(user?.email),
    [user?.email]
  );

  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [voiceControlStatus, setVoiceControlStatus] = useState(
    DEFAULT_PREFERENCES.voiceControlEnabled
      ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
      : VOICE_CONTROL_STATUS.DISABLED
  );
  const [voiceControlBusy, setVoiceControlBusy] = useState(false);
  const voiceControlRef = useRef(null);
  const voiceControlStatusRef = useRef(voiceControlStatus);

  const ensureVoiceControl = useCallback(() => {
    if (!voiceControlRef.current) {
      voiceControlRef.current = createVoiceControlController({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        shouldProcessCommand: () => false,
      });
    } else {
      voiceControlRef.current.updateOptions({
        language: getVoiceRecognitionLanguage(language),
        onStatusChange: setVoiceControlStatus,
        shouldProcessCommand: () => false,
      });
    }
    return voiceControlRef.current;
  }, [language]);

  useEffect(() => {
    const raw = localStorage.getItem(preferencesStorageKey);
    const parsed = parsePreferences(raw);
    setPreferences(parsed || DEFAULT_PREFERENCES);
  }, [preferencesStorageKey]);

  useEffect(
    () => () => {
      voiceControlRef.current?.destroy();
      voiceControlRef.current = null;
    },
    []
  );

  useEffect(() => {
    voiceControlStatusRef.current = voiceControlStatus;
  }, [voiceControlStatus]);

  useEffect(() => {
    if (!preferences.voiceControlEnabled) {
      const currentVoiceControlStatus = voiceControlStatusRef.current;
      const shouldKeepFailureStatus = [
        VOICE_CONTROL_STATUS.PERMISSION_DENIED,
        VOICE_CONTROL_STATUS.UNAVAILABLE,
        VOICE_CONTROL_STATUS.UNSUPPORTED,
        VOICE_CONTROL_STATUS.ERROR,
      ].includes(currentVoiceControlStatus);
      voiceControlRef.current?.stop();
      setVoiceControlStatus(
        shouldKeepFailureStatus ? currentVoiceControlStatus : VOICE_CONTROL_STATUS.DISABLED
      );
      return undefined;
    }

    const controller = ensureVoiceControl();
    let cancelled = false;
    controller.start({ requestPermission: false }).then(() => {
      if (cancelled) controller.stop();
    });

    return () => {
      cancelled = true;
    };
  }, [ensureVoiceControl, preferences.voiceControlEnabled]);

  const persistPreferences = (nextPreferences) => {
    writeUserPreferences(user?.email, nextPreferences);
  };

  // Cada cambio se guarda al momento; ya no hay botón de guardar.
  const togglePreference = (key) => {
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        [key]: !prev[key],
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const setBlockAnimationSpeedPreference = (rawValue) => {
    const nextValue = normalizeBlockAnimationSpeed(rawValue);
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        blockAnimationSpeed: nextValue,
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const setVoicePreference = (enabled) => {
    setPreferences((prev) => {
      const nextPreferences = {
        ...prev,
        voiceControlEnabled: enabled === true,
      };
      persistPreferences(nextPreferences);
      return nextPreferences;
    });
  };

  const confirmVoiceMicrophonePermission = useCallback(
    () =>
      new Promise((resolve) => {
        Modal.confirm({
          title: ui("voicePermissionConfirmTitle"),
          content: ui("voicePermissionConfirmDescription"),
          okText: ui("voicePermissionConfirmOk"),
          cancelText: ui("voicePermissionConfirmCancel"),
          centered: true,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [ui]
  );

  const toggleVoiceControl = async () => {
    if (voiceControlBusy) return;

    if (preferences.voiceControlEnabled) {
      voiceControlRef.current?.stop();
      setVoicePreference(false);
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      return;
    }

    const confirmed = await confirmVoiceMicrophonePermission();
    if (!confirmed) {
      setVoicePreference(false);
      setVoiceControlStatus(VOICE_CONTROL_STATUS.DISABLED);
      notification.info({
        message: ui("voiceControl"),
        description: ui("voicePermissionCancelledDescription"),
      });
      return;
    }

    setVoiceControlBusy(true);
    const controller = ensureVoiceControl();
    const started = await controller.start({ requestPermission: true });
    setVoiceControlBusy(false);

    if (!started) {
      setVoicePreference(false);
      const denied =
        controller.status === VOICE_CONTROL_STATUS.PERMISSION_DENIED ||
        voiceControlStatus === VOICE_CONTROL_STATUS.PERMISSION_DENIED;
      notification.error({
        message: ui("voiceControl"),
        description: denied
          ? ui("voicePermissionDeniedDescription")
          : ui("voiceUnavailableDescription"),
      });
      return;
    }

    setVoicePreference(true);
  };

  const currentBlockAnimationSpeed = normalizeBlockAnimationSpeed(
    preferences.blockAnimationSpeed ?? DEFAULT_BLOCK_ANIMATION_SPEED
  );
  const blockAnimationSpeedStatus =
    currentBlockAnimationSpeed <= 0.75
      ? ui("blockAnimationSlow")
      : currentBlockAnimationSpeed >= 1.25
        ? ui("blockAnimationFast")
        : ui("blockAnimationNormal");
  const blockAnimationSpeedPercent = Math.round(
    ((currentBlockAnimationSpeed - MIN_BLOCK_ANIMATION_SPEED) /
      Math.max(0.01, MAX_BLOCK_ANIMATION_SPEED - MIN_BLOCK_ANIMATION_SPEED)) *
      100
  );

  const resetPreferences = () => {
    localStorage.removeItem(preferencesStorageKey);
    writeGlobalBlockAnimationSpeed(DEFAULT_PREFERENCES.blockAnimationSpeed);
    setPreferences(DEFAULT_PREFERENCES);
    setVoiceControlStatus(
      DEFAULT_PREFERENCES.voiceControlEnabled
        ? VOICE_CONTROL_STATUS.AWAITING_PERMISSION
        : VOICE_CONTROL_STATUS.DISABLED
    );
    notification.info({
      message: i18nT("settings.ajustesRestablecidos"),
      description: i18nT("settings.seRestauraronLosValoresPorDefecto"),
    });
  };

  const buildSupportMailtoUrl = (subject, message) => {
    const fromEmail = String(user?.email || "").trim();
    const preamble = fromEmail ? `Usuario: ${fromEmail}\n\n` : "";
    const body = `${preamble}${message}`;
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const sendSupportEmail = async () => {
    const subject = supportSubject.trim();
    const message = supportMessage.trim();

    if (!subject || !message) {
      notification.warning({
        message: i18nT("settings.completaElFormulario"),
        description: i18nT("settings.debesEscribirAsuntoYDescripcionAntesDeEnviar"),
      });
      return;
    }

    setSupportSending(true);
    try {
      await axios.post("/support/email", {
        subject,
        message,
      });

      notification.success({
        message: i18nT("settings.mensajeEnviado"),
        description: `Tu solicitud fue enviada a ${SUPPORT_EMAIL}.`,
      });
      setSupportSubject("");
      setSupportMessage("");
    } catch (error) {
      const apiMessage = String(error?.response?.data?.message || "").trim();
      const canUseFallbackMailClient = typeof window !== "undefined";

      if (canUseFallbackMailClient) {
        window.location.href = buildSupportMailtoUrl(subject, message);
      }

      notification.error({
        message: i18nT("settings.noSePudoEnviarAutomaticamente"),
        description:
          apiMessage ||
          `Abrimos tu app de correo para completar el envio a ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setSupportSending(false);
    }
  };

  const voiceStatusText = getVoiceControlStatusText(voiceControlStatus, language);

  const voiceShowsState =
    preferences.voiceControlEnabled || voiceControlStatus !== VOICE_CONTROL_STATUS.DISABLED;

  const copySupportEmail = async () => {
    let copied = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(SUPPORT_EMAIL);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }
    if (!copied) {
      try {
        const field = document.createElement("textarea");
        field.value = SUPPORT_EMAIL;
        field.setAttribute("readonly", "");
        field.style.position = "fixed";
        field.style.opacity = "0";
        document.body.appendChild(field);
        field.select();
        copied = document.execCommand("copy");
        document.body.removeChild(field);
      } catch (error) {
        copied = false;
      }
    }
    if (copied) {
      notification.success({ message: ui("emailCopied"), description: SUPPORT_EMAIL, duration: 2 });
    } else {
      notification.info({ message: ui("supportEmail"), description: SUPPORT_EMAIL });
    }
  };

  const renderToggleRow = ({ key, icon, tone, title, description, on, onText, offText, onClick, busy, extra }) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`xaj-row xaj-row-toggle ${on ? "is-on" : "is-off"}${busy ? " is-busy" : ""}`}
      aria-pressed={Boolean(on)}
    >
      <span className={`xaj-ico xaj-ico-${tone}`} aria-hidden="true">
        {icon}
      </span>
      <span className="xaj-row-copy">
        <b>{title}</b>
        <small>{description}</small>
        {extra}
      </span>
      <span className="xaj-row-state">{on ? onText : offText}</span>
      <span className="xaj-toggle" aria-hidden="true" />
    </button>
  );

  return (
    <div className="xlm-root xaj-root">
      <ModernSidebar
        language={language}
        active="settings"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main xaj-main">
        <div className="xaj-shell">
          <button
            type="button"
            onClick={() => navigate(settingsReturnRoute)}
            className="xaj-back"
          >
            ‹ {ui("backToLobby")}
          </button>

          <header className="xaj-head">
            <span className="xaj-gear" aria-hidden="true">
              <IoCogOutline />
            </span>
            <div>
              <h1>{tKey("AJUSTES")}</h1>
              <p>{ui("sectionDescription")}</p>
            </div>
          </header>

          <div className="xaj-grid">
            <section className="xaj-card xaj-prefs" ref={generalRef}>
              <div className="xaj-card-head">
                <div>
                  <h2 className="xaj-card-title">
                    <span className="xaj-tone-neon" aria-hidden="true">
                      <IoCogOutline />
                    </span>
                    {ui("gameTitle")}
                  </h2>
                  <p className="xaj-card-sub">{ui("deviceOnly")}</p>
                </div>
                <button type="button" onClick={resetPreferences} className="xaj-btn xaj-btn-gold">
                  <IoRefreshOutline aria-hidden="true" />
                  <span>{ui("reset")}</span>
                </button>
              </div>

              <div className="xaj-rows">
                {renderToggleRow({
                  key: "sound",
                  icon: <IoVolumeHighOutline />,
                  tone: "green",
                  title: ui("gameSound"),
                  description: ui("gameSoundDescription"),
                  on: preferences.soundEnabled,
                  onText: ui("activated"),
                  offText: ui("deactivated"),
                  onClick: () => togglePreference("soundEnabled"),
                })}
                {renderToggleRow({
                  key: "animations",
                  icon: <IoCubeOutline />,
                  tone: "purple",
                  title: ui("animations"),
                  description: ui("animationsDescription"),
                  on: preferences.animationsEnabled,
                  onText: ui("activatedPlural"),
                  offText: ui("deactivatedPlural"),
                  onClick: () => togglePreference("animationsEnabled"),
                })}
                {renderToggleRow({
                  key: "camera",
                  icon: <IoCameraOutline />,
                  tone: "cyan",
                  title: ui("cameraFollowShort"),
                  description: ui("cameraFollowDescription"),
                  on: preferences.cameraFollowEnabled,
                  onText: ui("activated"),
                  offText: ui("deactivated"),
                  onClick: () => togglePreference("cameraFollowEnabled"),
                })}
                {renderToggleRow({
                  key: "premove",
                  icon: <IoFlashOutline />,
                  tone: "green",
                  title: ui("premove"),
                  description: ui("premoveDescription"),
                  on: preferences.premoveEnabled !== false,
                  onText: ui("activated"),
                  offText: ui("deactivated"),
                  onClick: () => togglePreference("premoveEnabled"),
                })}

                <div className="xaj-row xaj-row-speed">
                  <span className="xaj-ico xaj-ico-pink" aria-hidden="true">
                    <IoSpeedometerOutline />
                  </span>
                  <span className="xaj-row-copy">
                    <b>{ui("blockAnimationSpeed")}</b>
                    <small>{ui("blockAnimationSpeedDescription")}</small>
                  </span>
                  <span className="xaj-slider">
                    <span className="xaj-slider-value">{blockAnimationSpeedStatus}</span>
                    <input
                      type="range"
                      min={MIN_BLOCK_ANIMATION_SPEED}
                      max={MAX_BLOCK_ANIMATION_SPEED}
                      step="0.05"
                      value={currentBlockAnimationSpeed}
                      onChange={(event) => setBlockAnimationSpeedPreference(event.target.value)}
                      aria-label={ui("blockAnimationSpeed")}
                      aria-valuetext={blockAnimationSpeedStatus}
                      className="xaj-range"
                      style={{ "--xaj-range-progress": `${blockAnimationSpeedPercent}%` }}
                    />
                    <span className="xaj-slider-labels" aria-hidden="true">
                      <span>{ui("blockAnimationSlow")}</span>
                      <span>{ui("blockAnimationNormal")}</span>
                      <span>{ui("blockAnimationFast")}</span>
                    </span>
                  </span>
                </div>

                {renderToggleRow({
                  key: "voice",
                  icon: <IoMicOutline />,
                  tone: "red",
                  title: ui("voiceControl"),
                  description: ui("voiceControlShortDescription"),
                  on: preferences.voiceControlEnabled,
                  onText: ui("activated"),
                  offText: ui("deactivated"),
                  onClick: toggleVoiceControl,
                  busy: voiceControlBusy,
                  extra: voiceShowsState ? (
                    <small className="xaj-voice-state">{voiceStatusText}</small>
                  ) : null,
                })}
              </div>
            </section>

            <div className="xaj-side">
              <section className="xaj-card" ref={languageRef}>
                <h2 className="xaj-card-title">
                  <span className="xaj-tone-cyan" aria-hidden="true">
                    <IoGlobeOutline />
                  </span>
                  {tKey("IDIOMA")}
                </h2>
                <p className="xaj-card-sub">{ui("languageDescription")}</p>
                <div className="xaj-langs">
                  {LANGUAGE_OPTIONS.map((option) => {
                    const isActive = language === option.value;
                    const visual = SETTINGS_LANGUAGE_VISUALS[option.value] || {
                      title: option.nativeLabel,
                      description: getLanguageCopy(language, option.description),
                    };
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setLanguage(option.value)}
                        className={`xaj-lang${isActive ? " is-on" : ""}`}
                        aria-pressed={isActive}
                        title={visual.description}
                      >
                        <span className={`xaj-flag xaj-flag-${option.value}`} aria-hidden="true" />
                        <b>{visual.title}</b>
                        <span className="xaj-lang-code">{option.shortLabel}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="xaj-card" ref={supportRef}>
                <div className="xaj-card-head xaj-support-head">
                  <div>
                    <h2 className="xaj-card-title">
                      <span className="xaj-tone-purple" aria-hidden="true">
                        <IoHeadsetOutline />
                      </span>
                      {tKey("SOPORTE")}
                    </h2>
                    <p className="xaj-card-sub">{ui("supportDescription")}</p>
                  </div>
                  <button
                    type="button"
                    className="xaj-mail"
                    onClick={copySupportEmail}
                    title={ui("copyEmail")}
                    aria-label={`${ui("copyEmail")}: ${SUPPORT_EMAIL}`}
                  >
                    <IoMailOutline className="xaj-tone-purple" aria-hidden="true" />
                    <b>{SUPPORT_EMAIL}</b>
                    <IoCopyOutline className="xaj-mail-copy" aria-hidden="true" />
                  </button>
                </div>

                <label className="xaj-field">
                  <span className="xaj-label">{ui("subject")}</span>
                  <input
                    className="xaj-input"
                    value={supportSubject}
                    onChange={(event) => setSupportSubject(event.target.value)}
                    placeholder={ui("subjectPlaceholder")}
                  />
                </label>

                <label className="xaj-field">
                  <span className="xaj-label">{ui("describeProblem")}</span>
                  <textarea
                    className="xaj-input xaj-textarea"
                    value={supportMessage}
                    onChange={(event) => setSupportMessage(event.target.value)}
                    placeholder={ui("describeProblemPlaceholder")}
                    rows={4}
                  />
                </label>

                <button
                  type="button"
                  onClick={sendSupportEmail}
                  disabled={supportSending}
                  className="xaj-btn xaj-btn-neon"
                >
                  <IoPaperPlaneOutline aria-hidden="true" />
                  <span>{supportSending ? ui("sending") : tKey("ENVIAR")}</span>
                </button>
              </section>
            </div>
          </div>

          <div className="xaj-note">
            <IoInformationCircleOutline aria-hidden="true" />
            <span>{ui("autoSaveNote")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
